"use client";

import { useState } from "react";

import { ArrowRight, Check, Mic, Sparkles } from "lucide-react";

import { Reveal } from "@/components/site/reveal";
import { Button } from "@/components/ui/button";
import { APP_CONFIG } from "@/config/app-config";
import { formatCOP, type PlanRow } from "@/lib/precios";
import { cn } from "@/lib/utils";

const SISTEMAS = [
  { id: "pos", label: "POS" },
  { id: "contabilidad", label: "Contabilidad" },
  { id: "nomina", label: "Nómina" },
] as const;

/** Qué resuelve cada nivel — el catálogo no tiene columna de descripción, y una tarjeta con solo
 *  nombre y precio no ayuda a elegir. */
const SUBTITULO: Record<string, string> = {
  emprendedor: "Para empezar a ordenar el negocio",
  negocio: "Para el negocio que ya está creciendo",
  empresarial: "Para operaciones con varias sedes o empresas",
  plus: "Para la empresa que ya opera a gran volumen",
};

/** Ahorro real del plan anual frente a pagar 12 meses sueltos, calculado con los precios de la
 *  base — nunca un porcentaje escrito a mano. */
function ahorroAnual(plans: readonly PlanRow[]): number | null {
  const porcentajes = plans
    .filter((p) => Number(p.precio_mensual) > 0 && Number(p.precio_anual) > 0)
    .map((p) => 1 - Number(p.precio_anual) / (Number(p.precio_mensual) * 12));
  if (!porcentajes.length) return null;
  const menor = Math.min(...porcentajes);
  return menor > 0.005 ? Math.round(menor * 100) : null;
}

export function PricingTabs({
  plans,
  sistemaInicial = "pos",
}: {
  readonly plans: readonly PlanRow[];
  readonly sistemaInicial?: string;
}) {
  const [sistema, setSistema] = useState<string>(sistemaInicial);
  const [ciclo, setCiclo] = useState<"mensual" | "anual">("mensual");

  const ahorro = ahorroAnual(plans);
  const delSistema = plans.filter((p) => p.sistema === sistema);

  return (
    <div className="flex flex-col gap-8">
      {/* Selector de sistema + ciclo de cobro */}
      <div className="flex flex-col items-center gap-4">
        <div
          role="tablist"
          aria-label="Sistema"
          className="inline-flex rounded-full border bg-muted/60 p-1"
        >
          {SISTEMAS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={sistema === s.id}
              onClick={() => setSistema(s.id)}
              className={cn(
                "rounded-full px-5 py-1.5 font-medium text-sm transition-colors",
                sistema === s.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div
            role="tablist"
            aria-label="Ciclo de cobro"
            className="inline-flex rounded-full border bg-muted/60 p-1"
          >
            {(["mensual", "anual"] as const).map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={ciclo === c}
                onClick={() => setCiclo(c)}
                className={cn(
                  "rounded-full px-4 py-1 text-sm capitalize transition-colors",
                  ciclo === c
                    ? "bg-background font-medium text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {c}
              </button>
            ))}
          </div>
          {ahorro ? (
            <span className="rounded-full bg-primary/10 px-2.5 py-1 font-medium text-primary text-xs">
              Ahorra {ahorro}% al año
            </span>
          ) : null}
        </div>
      </div>

      {/* Tarjetas */}
      {/* 4 tarjetas desde el nivel `plus` (2026-09-08): 2×2 en pantalla mediana y las 4 en fila solo
          en `xl`. Con 4 columnas en `md` el precio y las features quedan ilegibles. */}
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 xl:grid-cols-4">
        {delSistema.map((plan, i) => {
          const precio = ciclo === "anual" ? Number(plan.precio_anual) : Number(plan.precio_mensual);
          const equivalenteMes = Math.round(Number(plan.precio_anual) / 12);

          return (
            <Reveal key={`${plan.sistema}-${plan.nivel}`} retraso={i * 70}>
              <div
                className={cn(
                  "relative flex h-full flex-col gap-6 rounded-3xl border bg-card p-7 transition-all duration-200",
                  plan.destacado
                    ? "border-primary/60 shadow-primary/10 shadow-xl md:-translate-y-3"
                    : "border-border/70 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg",
                )}
              >
                {plan.destacado ? (
                  <span className="-top-3 -translate-x-1/2 absolute left-1/2 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 py-1 font-medium text-[11px] text-primary-foreground">
                    <Sparkles className="size-3" />
                    El más elegido
                  </span>
                ) : null}

                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-lg">{plan.nombre}</h3>
                  <p className="text-muted-foreground text-sm leading-snug">
                    {SUBTITULO[plan.nivel] ?? ""}
                  </p>
                </div>

                <div className="flex flex-col gap-1">
                  <p className="flex items-baseline gap-1.5">
                    <span className="font-semibold text-4xl tabular-nums tracking-tight">
                      {formatCOP(precio)}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      /{ciclo === "anual" ? "año" : "mes"}
                    </span>
                  </p>
                  <p className="min-h-5 text-muted-foreground text-xs">
                    {ciclo === "anual"
                      ? `Equivale a ${formatCOP(equivalenteMes)} al mes`
                      : "Facturado mes a mes, cancelas cuando quieras"}
                  </p>
                  {/* El tope de ingresos es lo que de verdad separa un nivel del siguiente (las facturas
                      son ilimitadas en los 4). Nómina no lo usa —allá se segmenta por cantidad de
                      empleados— y por eso la columna admite `null`: sin valor, no se pinta la línea. */}
                  {plan.limite_ingresos_mes ? (
                    <p className="text-muted-foreground text-xs">
                      Ingresos hasta{" "}
                      <span className="font-medium text-foreground">
                        {formatCOP(Number(plan.limite_ingresos_mes))}
                      </span>{" "}
                      al mes
                    </p>
                  ) : null}
                </div>

                <Button
                  className="w-full"
                  variant={plan.destacado ? "default" : "outline"}
                  size="lg"
                  asChild
                >
                  <a href={APP_CONFIG.urls.register} className="gap-1.5">
                    Probar {plan.sistema === "pos" ? "1 mes" : "15 días"} gratis
                    <ArrowRight className="size-4" />
                  </a>
                </Button>

                <ul className="flex flex-col gap-3 border-t pt-5 text-sm">
                  {/* 📸 El cupo de fotos con IA sale de la columna del plan (lo cambia Admin), no de `features`. */}
                  {plan.limite_fotos_ia_mes > 0 ? (
                    <li className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Sparkles className="size-2.5 text-primary" />
                      </span>
                      <span className="text-muted-foreground">
                        {plan.limite_fotos_ia_mes.toLocaleString("es-CO")} fotos de producto con IA al mes
                      </span>
                    </li>
                  ) : null}
                  {/* 🎙️ El asistente de voz (2026-10-06): el Básico es gratis en todo plan del POS; el Premium trae los
                      minutos de la columna del plan (lo cambia Admin). */}
                  {plan.sistema === "pos" ? (
                    <li className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Mic className="size-2.5 text-primary" />
                      </span>
                      <span className="text-muted-foreground">
                        Asistente de voz «Oye Vecipp»: Básico gratis y sin límite
                        {Number(plan.limite_minutos_voz_mes) > 0
                          ? `, y ${Number(plan.limite_minutos_voz_mes).toLocaleString("es-CO")} minutos Premium al mes`
                          : ""}
                      </span>
                    </li>
                  ) : null}
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Check className="size-2.5 text-primary" />
                      </span>
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          );
        })}
      </div>

      <p className="text-center text-muted-foreground text-sm">
        Todos los planes incluyen prueba gratis (1 mes en el POS y 15 días en Contabilidad y Nómina), actualizaciones, soporte en español y el
        Centro de ayuda · Sin permanencia mínima
      </p>
    </div>
  );
}
